import { Component } from '@theme/component';
import { gsap, ScrollTrigger } from '@theme/gsap';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component
{
   requiredRefs = ['videoSources', 'videoElement'];
   #scrollTrigger;
   #onMetadataLoaded;
   #isScrollControlled = false;
   #lastAppliedTime = -1;
   #abortController = new AbortController();

   get #shouldLoop()
   {
      return this.dataset.videoLoop !== 'false';
   }

   connectedCallback()
   {
      super.connectedCallback();

      const { videoSources, videoElement } = this.refs;

      for (const source of videoSources)
      {
         const { videoSource } = source.dataset;

         if (videoSource) source.setAttribute('src', videoSource);
      }

      videoElement.load();

      this.#isScrollControlled = this.dataset.scrollControlVideo === 'true';
      if (this.#isScrollControlled)
      {
         this.#setupScrollControl();
      } else
      {
         this.#enableAutoplayPlayback();
      }
   }

   disconnectedCallback()
   {
      super.disconnectedCallback();

      this.#teardownScrollControl();
      this.#abortController.abort();
   }

   #setupScrollControl()
   {
      const { videoElement } = this.refs;
      if (!(videoElement instanceof HTMLVideoElement)) return;

      const disableOnMobile = this.dataset.scrollVideoDisableMobile !== 'false';
      if (disableOnMobile && window.matchMedia('(max-width: 749px)').matches)
      {
         this.#enableAutoplayPlayback({ ensureAttributes: true });
         return;
      }
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches)
      {
         this.#enableAutoplayPlayback({ ensureAttributes: true });
         return;
      }

      videoElement.pause();
      videoElement.removeAttribute('autoplay');
      videoElement.removeAttribute('loop');

      this.#onMetadataLoaded = () =>
      {
         this.#createScrollTrigger();
      };

      if (videoElement.readyState >= 1 && Number.isFinite(videoElement.duration) && videoElement.duration > 0)
      {
         this.#createScrollTrigger();
      } else
      {
         videoElement.addEventListener('loadedmetadata', this.#onMetadataLoaded, { once: true });
      }
   }

   #teardownScrollControl()
   {
      if (this.#scrollTrigger)
      {
         this.#scrollTrigger.kill();
         this.#scrollTrigger = undefined;
      }

      const { videoElement } = this.refs;
      if (this.#onMetadataLoaded && videoElement instanceof HTMLVideoElement)
      {
         videoElement.removeEventListener('loadedmetadata', this.#onMetadataLoaded);
      }

      this.#onMetadataLoaded = undefined;
   }

   #applyLoopSettings(videoElement)
   {
      if (!(videoElement instanceof HTMLVideoElement)) return;

      const signal = this.#abortController.signal;

      if (this.#shouldLoop)
      {
         videoElement.loop = true;
         videoElement.setAttribute('loop', '');
         return;
      }

      videoElement.loop = false;
      videoElement.removeAttribute('loop');
      videoElement.addEventListener(
         'ended',
         () => {
            videoElement.pause();
         },
         { signal }
      );
   }

   #enableAutoplayPlayback({ ensureAttributes = false } = {})
   {
      const { videoElement } = this.refs;
      if (!(videoElement instanceof HTMLVideoElement)) return;

      if (ensureAttributes)
      {
         videoElement.setAttribute('autoplay', '');
      }

      this.#applyLoopSettings(videoElement);

      videoElement.muted = true;

      const playVideo = () =>
      {
         const playPromise = videoElement.play();
         if (playPromise && typeof playPromise.catch === 'function')
         {
            playPromise.catch(() => {});
         }
      };

      if (videoElement.readyState >= 2)
      {
         playVideo();
      } else
      {
         videoElement.addEventListener('canplay', playVideo, { once: true });
      }
   }

   #createScrollTrigger()
   {
      this.#teardownScrollControl();

      const { videoElement } = this.refs;
      if (!(videoElement instanceof HTMLVideoElement)) return;
      if (!Number.isFinite(videoElement.duration) || videoElement.duration <= 0) return;

      const startPercent = this.#parsePercentage(this.dataset.scrollVideoStartPercent, 0);
      const endPercent = this.#parsePercentage(this.dataset.scrollVideoEndPercent, 100);
      const scrub = this.#parseNumber(this.dataset.scrollVideoScrub, 0.5);
      const debugMarkers = this.dataset.scrollVideoDebugMarkers === 'true';
      const freezeAtEnd = this.dataset.scrollVideoOnce === 'true';
      const trigger = this.closest('.section') || this;
      const duration = videoElement.duration;
      const rangeStart = Math.min(startPercent, endPercent);
      const rangeEnd = Math.max(startPercent, endPercent);
      const startTime = duration * rangeStart;
      const endTime = duration * rangeEnd;

      this.#setVideoTime(startTime);
      videoElement.pause();

      this.#scrollTrigger = ScrollTrigger.create({
         trigger,
         start: this.dataset.scrollVideoTriggerStart || 'top bottom',
         end: this.dataset.scrollVideoTriggerEnd || 'bottom top',
         scrub,
         markers: debugMarkers,
         onUpdate: (self) =>
         {
            const targetTime = gsap.utils.interpolate(startTime, endTime, self.progress);
            this.#setVideoTime(targetTime);
         },
         onLeave: () =>
         {
            if (!freezeAtEnd) return;

            this.#setVideoTime(endTime, true);
         },
         onLeaveBack: () =>
         {
            this.#setVideoTime(startTime, true);
         },
      });
   }

   #setVideoTime(targetTime, force = false)
   {
      const { videoElement } = this.refs;
      if (!(videoElement instanceof HTMLVideoElement)) return;

      const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
      const minDelta = isFirefox ? 0.04 : 0.016;
      if (!force && this.#lastAppliedTime >= 0 && Math.abs(targetTime - this.#lastAppliedTime) < minDelta) return;

      // Subtract a small amount from duration to prevent the video from reaching the 'ended' state.
      // On some browsers (like Safari), reaching the exact end freezes the video and ignores subsequent currentTime updates.
      const maxTime = videoElement.duration ? Math.max(0, videoElement.duration - 0.05) : targetTime;
      const clampedTime = Math.min(maxTime, Math.max(0, targetTime));

      if (typeof videoElement.fastSeek === 'function' && isFirefox)
      {
         try
         {
            videoElement.fastSeek(clampedTime);
         } catch
         {
            videoElement.currentTime = clampedTime;
         }
      } else
      {
         videoElement.currentTime = clampedTime;
      }

      this.#lastAppliedTime = clampedTime;
   }

   #parsePercentage(value, fallback)
   {
      const parsed = this.#parseNumber(value, fallback);
      return Math.min(1, Math.max(0, parsed / 100));
   }

   #parseNumber(value, fallback)
   {
      const parsed = Number.parseFloat(value ?? '');
      return Number.isFinite(parsed) ? parsed : fallback;
   }
}

if (!customElements.get('video-background-component'))
{
   customElements.define('video-background-component', VideoBackgroundComponent);
}
